import React, { useEffect, useState } from 'react';
import { FilteredRolodexView } from "./FilteredRolodexView";
import { navigate, safeFetch } from "./App";
import { ResponsiveImage } from "./components/ResponsiveImage";
import { CheckCircle2, XCircle, Crown, Shield, Briefcase, Calendar, Store, Handshake, Mail, Users, Trash2, Edit2, Globe, Lock, UserPlus, Link2, Copy, Check, Infinity, Heart, Share2, MessageCircle, Send, Settings } from 'lucide-react';

export const UserBusinesses = ({ chatId }) => {
    const [businesses, setBusinesses] = useState([]);
    const [events, setEvents] = useState([]);
    const [barters, setBarters] = useState([]);
    const [stores, setStores] = useState([]);
    const [zones, setZones] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [activeTab, setActiveTab] = useState('businesses');
    const [posts, setPosts] = useState([]);
    const [groups, setGroups] = useState([]);
    const [userInfo, setUserInfo] = useState(null);
    const [editingZone, setEditingZone] = useState(null);
    const [editZoneName, setEditZoneName] = useState('');
    const [editZoneDescription, setEditZoneDescription] = useState('');
    const [editZoneIsPrivate, setEditZoneIsPrivate] = useState(false);
    const [invitingZone, setInvitingZone] = useState(null);
    const [inviteUsageLimit, setInviteUsageLimit] = useState('unlimited');
    const [inviteMaxUses, setInviteMaxUses] = useState(10);
    const [generatedInviteLink, setGeneratedInviteLink] = useState(null);
    const [linkCopied, setLinkCopied] = useState(false);
    const [mainTab, setMainTab] = useState('my-content'); // 'my-content' or 'favorites'
    const [favoritesLists, setFavoritesLists] = useState([]);
    const [selectedFavoritesList, setSelectedFavoritesList] = useState(null);
    const [expandedFavoritesData, setExpandedFavoritesData] = useState(null);
    const [loadingFavoritesData, setLoadingFavoritesData] = useState(false);
    const [sharingList, setSharingList] = useState(null);
    const [shareLinkCopied, setShareLinkCopied] = useState(false);
    const [editingFavoritesList, setEditingFavoritesList] = useState(null);
    const [editListName, setEditListName] = useState('');


    const handleGroupDeleted = async (group_id) => {
        setGroups(prev => prev.filter(group => group.group_id !== group_id));
    };

    const handleDeleteZone = async (zoneId) => {
        if (!window.confirm('האם אתה בטוח שברצונך למחוק את הקהילה? פעולה זו אינה ניתנת לביטול.')) return;

        try {
            await safeFetch('https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/delete-zone', chatId, {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ chat_id: chatId, zone_id: zoneId }),
            });

            setZones(prev => prev.filter(z => z.zone_id !== zoneId));
        } catch (err) {
            console.error('❌ Failed to delete zone:', err);
            alert('שגיאה במחיקת הקהילה');
        }
    };

    const handleEditZone = (zone) => {
        setEditingZone(zone);
        // Edit the display name, not the zone_id
        setEditZoneName(zone.zone_display_name || zone.zone_id);
        setEditZoneDescription(zone.description || '');
        setEditZoneIsPrivate(zone.is_private || false);
    };

    const handleUpdateZone = async () => {
        if (!editZoneName.trim() || !editZoneDescription.trim()) {
            alert('נא למלא את כל השדות');
            return;
        }

        try {
            await safeFetch('https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/update-zone', chatId, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    chat_id: chatId,
                    zone_id: editingZone.zone_id,
                    new_zone_name: editZoneName.trim(),
                    description: editZoneDescription.trim(),
                    is_private: editZoneIsPrivate
                }),
            });

            // Update local state - only update display name, not zone_id
            setZones(prev => prev.map(z =>
                z.zone_id === editingZone.zone_id
                    ? { ...z, zone_display_name: editZoneName.trim(), description: editZoneDescription.trim(), is_private: editZoneIsPrivate }
                    : z
            ));

            setEditingZone(null);
        } catch (err) {
            console.error('❌ Failed to update zone:', err);
            alert('שגיאה בעדכון הקהילה');
        }
    };

    const handleGenerateInviteLink = async () => {
        if (inviteUsageLimit === 'limited' && (!inviteMaxUses || inviteMaxUses < 1)) {
            alert('נא להזין מספר שימושים תקין');
            return;
        }

        try {
            const response = await safeFetch('https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/generate-zone-invite', chatId, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    chat_id: chatId,
                    zone_id: invitingZone.zone_id,
                    max_uses: inviteUsageLimit === 'unlimited' ? null : inviteMaxUses
                }),
            });

            const data = await response.json();
            setGeneratedInviteLink(data.invite_link);
        } catch (err) {
            console.error('❌ Failed to generate invite link:', err);
            alert('שגיאה ביצירת קישור ההזמנה');
        }
    };

    const handleCopyInviteLink = async () => {
        try {
            await navigator.clipboard.writeText(generatedInviteLink);
            setLinkCopied(true);
            setTimeout(() => setLinkCopied(false), 2000);
        } catch (err) {
            console.error('❌ Failed to copy link:', err);
            alert('שגיאה בהעתקת הקישור');
        }
    };

    const handleCloseInviteModal = () => {
        setInvitingZone(null);
        setInviteUsageLimit('unlimited');
        setInviteMaxUses(10);
        setGeneratedInviteLink(null);
        setLinkCopied(false);
    };

    const fetchFavoritesLists = async () => {
        if (!chatId || chatId === 'guest') return;

        try {
            const res = await safeFetch(
                `https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/user-favorites-lists?chat_id=${chatId}`,
                chatId
            );
            const data = await res.json();
            const parsedLists = (data || []).map(list => ({
                ...list,
                data: typeof list.data === 'string' ? JSON.parse(list.data) : list.data
            }));
            console.log('📋 Parsed favorites lists:', parsedLists);
            setFavoritesLists(parsedLists);
        } catch (err) {
            console.error('❌ Failed to fetch favorites lists:', err);
        }
    };

    const handleDeleteFavoritesList = async (listId) => {
        if (!window.confirm('האם אתה בטוח שברצונך למחוק את רשימת המועדפים? פעולה זו אינה ניתנת לביטול.')) return;

        try {
            await safeFetch(
                'https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/delete-user-favorites-list',
                chatId,
                {
                    method: 'DELETE',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ chat_id: chatId, list_id: listId }),
                }
            );

            setFavoritesLists(prev => prev.filter(l => l.playlist_id !== listId));
        } catch (err) {
            console.error('❌ Failed to delete favorites list:', err);
            alert('שגיאה במחיקת רשימת המועדפים');
        }
    };

    const handleEditFavoritesList = (list) => {
        setEditingFavoritesList(list);
        setEditListName(list.data?.name || '');
    };

    const handleUpdateFavoritesList = async () => {
        if (!editListName.trim()) {
            alert('נא למלא את שם הרשימה');
            return;
        }

        try {
            await safeFetch(
                'https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/update-favorites-list',
                chatId,
                {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        chat_id: chatId,
                        list_id: editingFavoritesList.playlist_id,
                        action: 'rename',
                        new_name: editListName.trim()
                    }),
                }
            );

            // Update local state
            setFavoritesLists(prev => prev.map(l =>
                l.playlist_id === editingFavoritesList.playlist_id
                    ? { ...l, data: { ...l.data, name: editListName.trim() } }
                    : l
            ));

            setEditingFavoritesList(null);
        } catch (err) {
            console.error('❌ Failed to update favorites list:', err);
            alert('שגיאה בעדכון רשימת המועדפים');
        }
    };

    const handleShareFavoritesList = (list) => {
        setSharingList(list);
    };

    const handleShareOption = async (option) => {
        if (!sharingList) return;

        try {
            // First, call the share-list endpoint to make the list shareable
            await safeFetch(
                'https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/share-list',
                chatId,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        chat_id: chatId,
                        list_id: sharingList.playlist_id
                    }),
                }
            );

            // Now generate and share the URL
            const shareUrl = `${window.location.origin}?view=favorites-list&list_id=${sharingList.playlist_id}`;
            const listName = sharingList.data?.name || 'רשימת מועדפים';
            const shareText = `${listName} - רשימת המועדפים שלי במקומית`;

            if (option === 'whatsapp') {
                const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(shareText + '\n' + shareUrl)}`;
                window.open(whatsappUrl, '_blank');
                setSharingList(null);
            } else if (option === 'telegram') {
                const telegramUrl = `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`;
                window.open(telegramUrl, '_blank');
                setSharingList(null);
            } else if (option === 'copy') {
                await navigator.clipboard.writeText(shareUrl);
                setShareLinkCopied(true);
                setTimeout(() => {
                    setShareLinkCopied(false);
                    setSharingList(null);
                }, 2000);
            }
        } catch (err) {
            console.error('Failed to share list:', err);
            alert('שגיאה בשיתוף הרשימה');
        }
    };

    const handleRemoveFromFavoritesList = async (entityId, entityType) => {
        if (!selectedFavoritesList) return;

        try {
            await safeFetch(
                'https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/update-favorites-list',
                chatId,
                {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        chat_id: chatId,
                        list_id: selectedFavoritesList.playlist_id,
                        entity_type: entityType,
                        entity_id: entityId,
                        action: 'remove'
                    }),
                }
            );

            // Update the expanded data by removing the entity
            setExpandedFavoritesData(prev => {
                if (!prev) return prev;
                return {
                    ...prev,
                    [entityType]: prev[entityType]?.filter(item => {
                        const itemId = entityType === 'groups' ? item.group_id : item.business_id;
                        return itemId !== entityId;
                    }) || []
                };
            });

            // Update the selected favorites list
            setSelectedFavoritesList(prev => {
                if (!prev) return prev;
                return {
                    ...prev,
                    data: {
                        ...prev.data,
                        [entityType]: prev.data[entityType]?.filter(id => id !== entityId) || []
                    }
                };
            });

            // Update the favorites lists array
            setFavoritesLists(prev => prev.map(list => {
                if (list.playlist_id === selectedFavoritesList.playlist_id) {
                    return {
                        ...list,
                        data: {
                            ...list.data,
                            [entityType]: list.data[entityType]?.filter(id => id !== entityId) || []
                        }
                    };
                }
                return list;
            }));
        } catch (err) {
            console.error('❌ Failed to remove from favorites list:', err);
            alert('שגיאה בהסרת הפריט מרשימת המועדפים');
        }
    };

    const fetchFavoritesListData = async (list) => {
        setLoadingFavoritesData(true);
        setExpandedFavoritesData(null);

        try {
            const businessIds = list.data?.businesses || [];
            const eventIds = list.data?.events || [];
            const barterIds = list.data?.barters || [];
            const groupIds = list.data?.groups || [];

            // Fetch all entities in parallel
            const [businessesData, eventsData, bartersData, groupsData] = await Promise.all([
                // Fetch businesses
                Promise.all(businessIds.map(async (id) => {
                    try {
                        const res = await safeFetch(`https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/business-info?business_id=${id}&chat_id=${chatId}`, chatId);
                        return await res.json();
                    } catch (err) {
                        console.error(`Failed to fetch business ${id}:`, err);
                        return null;
                    }
                })),
                // Fetch events
                Promise.all(eventIds.map(async (id) => {
                    try {
                        const res = await safeFetch(`https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/business-info?business_id=${id}&chat_id=${chatId}`, chatId);
                        return await res.json();
                    } catch (err) {
                        console.error(`Failed to fetch event ${id}:`, err);
                        return null;
                    }
                })),
                // Fetch barters
                Promise.all(barterIds.map(async (id) => {
                    try {
                        const res = await safeFetch(`https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/business-info?business_id=${id}&chat_id=${chatId}`, chatId);
                        return await res.json();
                    } catch (err) {
                        console.error(`Failed to fetch barter ${id}:`, err);
                        return null;
                    }
                })),
                // Fetch groups (assuming same endpoint pattern)
                Promise.all(groupIds.map(async (group_obj) => {
                    try {
                        const group_id = group_obj.group_id;
                        const res = await safeFetch(`https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/group-info?group_id=${group_id}&chat_id=${chatId}&zone_id=${group_obj.zone_id}`, chatId);
                        return await res.json();
                    } catch (err) {
                        console.error(`Failed to fetch group ${group_id}:`, err);
                        return null;
                    }
                })),
            ]);

            setExpandedFavoritesData({
                businesses: businessesData.filter(b => b !== null),
                events: eventsData.filter(e => e !== null),
                barters: bartersData.filter(b => b !== null),
                groups: groupsData.filter(g => g !== null),
            });
        } catch (err) {
            console.error('❌ Failed to fetch favorites list data:', err);
        } finally {
            setLoadingFavoritesData(false);
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                const entitiesRes = await safeFetch(`https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/user-entities?chat_id=${chatId}`);
                const entitiesData = await entitiesRes.json();

                setBusinesses(entitiesData['businesses']);
                setEvents(entitiesData['events']);
                setBarters(entitiesData['barters'] || []);
                setZones(entitiesData['zones'] || []);
                setPosts(entitiesData['public_posts']);
                setGroups(entitiesData['groups'] || []);
                setUserInfo(entitiesData['user_info'] || null);
                setLoading(false);

                // Fetch store data separately and in parallel (don't block initial render)
                const adminStoreIds = entitiesData['admin_stores'] || [];
                if (adminStoreIds.length > 0) {
                    // Limit concurrent requests to 3 to avoid overwhelming the server
                    const fetchWithConcurrency = async (ids, concurrency = 3) => {
                        const results = [];
                        for (let i = 0; i < ids.length; i += concurrency) {
                            const batch = ids.slice(i, i + concurrency);
                            const batchResults = await Promise.all(
                                batch.map(storeId =>
                                    safeFetch(`https://v3ugnvc3qk.execute-api.us-west-2.amazonaws.com/dev/get-store-by-id?store_id=${storeId}`, chatId)
                                        .then(res => res.json())
                                        .catch(err => {
                                            console.error(`Failed to fetch store ${storeId}:`, err);
                                            return null;
                                        })
                                )
                            );
                            results.push(...batchResults);
                        }
                        return results;
                    };

                    fetchWithConcurrency(adminStoreIds).then(storesData => {
                        setStores(storesData.filter(store => store !== null));
                    });
                } else {
                    setStores([]);
                }

            } catch (err) {
                console.error('❌ Failed to fetch user data:', err);
                setError('שגיאה בטעינת הנתונים');
                setLoading(false);
            }
        };

        fetchData();
        fetchFavoritesLists();
    }, [chatId]);


    // Tab definitions with Lucide icons
    const tabs = [
        { id: 'businesses', label: 'עסקים', icon: Briefcase },
        { id: 'events', label: 'מפגשים', icon: Calendar },
        { id: 'barters', label: 'ברטרים', icon: Handshake },
        { id: 'zones', label: 'קהילות', icon: Users },
        { id: 'stores', label: 'חנויות', icon: Store },
        // { id: 'posts', label: 'קהילה', icon: MessageSquare },
        // { id: 'groups', label: 'קבוצות', icon: Users },
    ];

    const handleDeletePost = async (postId) => {
        if (!window.confirm('האם אתה בטוח שברצונך למחוק את הפוסט?')) return;

        try {
            await safeFetch('https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/delete-post', chatId, {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ chat_id: chatId, post_id: postId, zone_id: ((posts.filter(post => post['post_id'] == postId))[0])['zone_id'] }),
            });

            // Refresh posts in UI
            setPosts(prev => prev.filter(p => p.post_id !== postId));
        } catch (err) {
            console.error('❌ Failed to delete post:', err);
            alert('שגיאה במחיקת הפוסט');
        }
    };


    if (loading) {
        return (
            <div className="flex justify-center items-center h-32">
                <div className="w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }

    if (error) return <p className="text-danger-500 text-center">{error}</p>;

    return (
        <div className="min-h-screen bg-gradient-to-br from-surface via-white to-surface">
            <div className="flex justify-center px-4 sm:px-6">
                <div className="w-full max-w-5xl pb-20">

                    {/* Hero Profile Section */}
                    {userInfo && (userInfo.email || userInfo.first_name || userInfo.last_name || userInfo.photo_url) && (
                        <div className="mx-4 sm:mx-8 mt-8 mb-8">
                            <div className="bg-white rounded-2xl shadow-lg border border-line-subtle p-6 sm:p-8">
                                <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
                                    {/* Profile Picture */}
                                    <div className="relative flex-shrink-0">
                                        {userInfo.photo_url ? (
                                            <div className="relative">
                                                <img
                                                    src={userInfo.photo_url}
                                                    alt="Profile"
                                                    className="w-24 h-24 sm:w-28 sm:h-28 rounded-2xl object-cover border-2 border-line-subtle shadow-lg"
                                                />
                                                <div className="absolute -bottom-1 -right-1 w-7 h-7 bg-gradient-to-br from-success-500 to-success-600 rounded-full border-3 border-white shadow-md flex items-center justify-center">
                                                    <CheckCircle2 size={14} className="text-white" strokeWidth={3} />
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="relative">
                                                <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-2xl bg-gradient-to-br from-secondary-500 to-secondary-700 flex items-center justify-center text-white text-3xl font-bold shadow-lg border-2 border-line-subtle">
                                                    {userInfo.first_name?.[0] || userInfo.email?.[0] || '?'}
                                                </div>
                                                <div className="absolute -bottom-1 -right-1 w-7 h-7 bg-gradient-to-br from-success-500 to-success-600 rounded-full border-3 border-white shadow-md flex items-center justify-center">
                                                    <CheckCircle2 size={14} className="text-white" strokeWidth={3} />
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Profile Info */}
                                    <div className="flex-1 text-center sm:text-right">
                                        {(userInfo.first_name || userInfo.last_name) && (
                                            <h1 className="text-2xl sm:text-3xl font-bold text-ink-strong mb-2">
                                                {userInfo.first_name} {userInfo.last_name}
                                            </h1>
                                        )}
                                        {userInfo.email && (
                                            <p className="text-sm text-ink-muted flex items-center justify-center sm:justify-start gap-2">
                                                <Mail size={15} className="text-secondary-500" />
                                                <span className="font-medium">{userInfo.email}</span>
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Content Section with Tabs */}
                    <div className="mx-4 sm:mx-8">
                        {/* Main Tabs - Level 1 */}
                        <div className="mb-8">
                            <div className="bg-white/60 backdrop-blur-sm rounded-2xl shadow-md p-2 border border-line-subtle inline-flex gap-2">
                                <button
                                    onClick={() => setMainTab('my-content')}
                                    className={`px-6 py-3 rounded-xl font-bold text-base transition-all duration-300 ${mainTab === 'my-content'
                                        ? 'bg-gradient-to-br from-secondary-500 to-secondary-700 text-white shadow-lg'
                                        : 'text-ink-muted hover:text-ink-strong hover:bg-surface-muted'
                                        }`}
                                >
                                    העשייה שלי
                                </button>
                                <button
                                    onClick={() => setMainTab('favorites')}
                                    className={`px-6 py-3 rounded-xl font-bold text-base transition-all duration-300 ${mainTab === 'favorites'
                                        ? 'bg-gradient-to-br from-secondary-500 to-secondary-700 text-white shadow-lg'
                                        : 'text-ink-muted hover:text-ink-strong hover:bg-surface-muted'
                                        }`}
                                >
                                    רשימות המועדפים שלי
                                </button>
                            </div>
                        </div>

                        {/* My Content Tab */}
                        {mainTab === 'my-content' && (
                            <>
                                {/* Premium App-Style Tabs Navigation */}
                                <div className="sticky top-16 z-30 bg-gradient-to-b from-surface via-surface to-transparent pt-2 pb-6 -mx-4 px-4 sm:-mx-8 sm:px-8">
                                    {/* Sleek Horizontal Scrollable Tab Bar */}
                                    <div className="relative">
                                        {/* Glass-morphism container */}
                                        <div className="bg-white/80 backdrop-blur-xl rounded-[28px] shadow-elevated p-2 border border-white/60 shadow-lg">
                                            {/* Tabs wrapper with horizontal scroll */}
                                            <div className="flex gap-2 overflow-x-auto scrollbar-hide scroll-smooth">
                                                {tabs.map(tab => {
                                                    const Icon = tab.icon;
                                                    const isActive = activeTab === tab.id;

                                                    // Get count for each tab
                                                    let count = 0;
                                                    if (tab.id === 'businesses') count = businesses.length;
                                                    else if (tab.id === 'events') count = events.length;
                                                    else if (tab.id === 'barters') count = barters.length;
                                                    else if (tab.id === 'stores') count = stores.length;
                                                    else if (tab.id === 'zones') count = zones.length;

                                                    return (
                                                        <button
                                                            key={tab.id}
                                                            onClick={() => setActiveTab(tab.id)}
                                                            className={`
                                                        group relative flex items-center gap-3 px-5 py-3.5 rounded-[20px]
                                                        font-semibold text-sm transition-all duration-500 ease-out
                                                        whitespace-nowrap flex-shrink-0
                                                        ${isActive
                                                                    ? 'bg-gradient-to-br from-secondary-500 via-[#d8a558] to-secondary-700 text-white shadow-elevated transform scale-[1.02]'
                                                                    : 'text-ink-muted hover:text-ink-strong hover:bg-gradient-to-br hover:from-surface-muted hover:to-surface-muted/70 hover:shadow-sm active:scale-95'
                                                                }
                                                    `}
                                                        >
                                                            {/* Animated background glow for active tab */}
                                                            {isActive && (
                                                                <div className="absolute inset-0 rounded-[20px] bg-gradient-to-br from-secondary-500 to-secondary-700 opacity-60 blur-lg animate-pulse"></div>
                                                            )}

                                                            {/* Icon with subtle animation */}
                                                            <div className={`relative z-10 transition-transform duration-300 ${isActive ? 'scale-110' : 'group-hover:scale-105'}`}>
                                                                <Icon
                                                                    size={20}
                                                                    strokeWidth={isActive ? 2.5 : 2}
                                                                    className={`${isActive ? 'drop-shadow-sm' : ''}`}
                                                                />
                                                            </div>

                                                            {/* Label and count */}
                                                            <div className="relative z-10 flex items-center gap-2">
                                                                <span className={`transition-all duration-300 ${isActive ? 'font-bold tracking-wide' : 'font-semibold'}`}>
                                                                    {tab.label}
                                                                </span>
                                                                {count > 0 && (
                                                                    <span className={`
                                                                inline-flex items-center justify-center min-w-[22px] h-[22px] px-2 rounded-full text-[11px] font-bold
                                                                transition-all duration-300
                                                                ${isActive
                                                                            ? 'bg-white/30 text-white backdrop-blur-md border border-white/30 shadow-inner'
                                                                            : 'bg-secondary-500/12 text-secondary-700 border border-secondary-500/20'
                                                                        }
                                                            `}>
                                                                        {count}
                                                                    </span>
                                                                )}
                                                            </div>

                                                            {/* Active indicator line (bottom) */}
                                                            {isActive && (
                                                                <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1/3 h-1 bg-white/40 rounded-full shadow-lg"></div>
                                                            )}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>

                                        {/* Subtle gradient fade on edges for scroll indication */}
                                        <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-surface to-transparent pointer-events-none rounded-l-[28px]"></div>
                                        <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-surface to-transparent pointer-events-none rounded-r-[28px]"></div>
                                    </div>

                                    {/* Optional: Active tab indicator text */}
                                    <div className="flex items-center justify-center mt-4 gap-2">
                                        <div className="h-1 w-1 rounded-full bg-secondary-500/30"></div>
                                        <span className="text-xs font-medium text-ink-subtle tracking-wide">
                                            {tabs.find(t => t.activeTab === activeTab)?.label || tabs.find(t => t.id === activeTab)?.label}
                                        </span>
                                        <div className="h-1 w-1 rounded-full bg-secondary-500/30"></div>
                                    </div>
                                </div>

                                {/* Tab Content */}
                                <div className="mt-6">
                                    {activeTab === 'businesses' && (
                                        <div className="animate-fadeIn">
                                            {businesses.length === 0 ? (
                                                <div className="text-center py-20 bg-white rounded-2xl shadow-sm border border-line-subtle">
                                                    <div className="w-24 h-24 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-secondary-500/10 to-secondary-700/10 flex items-center justify-center">
                                                        <Briefcase size={40} className="text-secondary-500" strokeWidth={1.5} />
                                                    </div>
                                                    <h3 className="text-xl font-bold text-ink-strong mb-2">אין עסקים עדיין</h3>
                                                    <p className="text-sm text-ink-muted mb-6">צור עסק חדש כדי להתחיל לנהל את הפעילות שלך</p>
                                                </div>
                                            ) : (
                                                <FilteredRolodexView
                                                    items={businesses}
                                                    chatId={chatId}
                                                    fromView='my-businesses'
                                                />
                                            )}
                                        </div>
                                    )}

                                    {activeTab === 'events' && (
                                        <div className="animate-fadeIn">
                                            {events.length === 0 ? (
                                                <div className="text-center py-20 bg-white rounded-2xl shadow-sm border border-line-subtle">
                                                    <div className="w-24 h-24 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-info-50 to-info-100/50 flex items-center justify-center">
                                                        <Calendar size={40} className="text-info-600" strokeWidth={1.5} />
                                                    </div>
                                                    <h3 className="text-xl font-bold text-ink-strong mb-2">אין מפגשים עדיין</h3>
                                                    <p className="text-sm text-ink-muted mb-6">צור מפגש חדש כדי לשתף עם הקהילה</p>
                                                </div>
                                            ) : (
                                                <FilteredRolodexView
                                                    items={events}
                                                    chatId={chatId}
                                                    fromView='my-businesses'
                                                />
                                            )}
                                        </div>
                                    )}

                                    {activeTab === 'barters' && (
                                        <div className="animate-fadeIn">
                                            {barters.length === 0 ? (
                                                <div className="text-center py-20 bg-white rounded-2xl shadow-sm border border-line-subtle">
                                                    <div className="w-24 h-24 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-success-50 to-success-100/50 flex items-center justify-center">
                                                        <Handshake size={40} className="text-success-600" strokeWidth={1.5} />
                                                    </div>
                                                    <h3 className="text-xl font-bold text-ink-strong mb-2">אין ברטרים עדיין</h3>
                                                    <p className="text-sm text-ink-muted mb-6">צור ברטר חדש כדי להחליף שירותים</p>
                                                </div>
                                            ) : (
                                                <FilteredRolodexView
                                                    items={barters}
                                                    chatId={chatId}
                                                    fromView='my-businesses'
                                                />
                                            )}
                                        </div>
                                    )}

                                    {activeTab === 'zones' && (
                                        <div className="animate-fadeIn">
                                            {zones.length === 0 ? (
                                                <div className="text-center py-20 bg-white rounded-2xl shadow-sm border border-line-subtle">
                                                    <div className="w-24 h-24 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-warning-50 to-warning-100/50 flex items-center justify-center">
                                                        <Users size={40} className="text-warning-600" strokeWidth={1.5} />
                                                    </div>
                                                    <h3 className="text-xl font-bold text-ink-strong mb-2">אין קהילות עדיין</h3>
                                                    <p className="text-sm text-ink-muted mb-6">צור קהילה חדשה כדי לאחד אנשים סביב נושא משותף</p>
                                                </div>
                                            ) : (
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                    {zones.map(zone => (
                                                        <div
                                                            key={zone.zone_id}
                                                            className="group relative bg-white rounded-2xl p-6 shadow-sm border border-line-subtle hover:shadow-lg transition-all duration-300 cursor-pointer hover:-translate-y-1"
                                                        >
                                                            {/* Action buttons */}
                                                            <div className="absolute top-3 left-3 flex gap-2 z-10">
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleEditZone(zone);
                                                                    }}
                                                                    className="bg-info-500 text-white rounded-full p-2 w-8 h-8 flex items-center justify-center shadow-lg hover:bg-info-600 transition-all transform hover:scale-110"
                                                                    title="ערוך קהילה"
                                                                >
                                                                    <Edit2 size={16} strokeWidth={2.5} />
                                                                </button>
                                                                {zone.is_private && (
                                                                    <button
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            setInvitingZone(zone);
                                                                        }}
                                                                        className="bg-success-500 text-white rounded-full p-2 w-8 h-8 flex items-center justify-center shadow-lg hover:bg-success-600 transition-all transform hover:scale-110"
                                                                        title="הזמן משתמשים"
                                                                    >
                                                                        <UserPlus size={16} strokeWidth={2.5} />
                                                                    </button>
                                                                )}
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleDeleteZone(zone.zone_id);
                                                                    }}
                                                                    className="bg-danger-500 text-white rounded-full p-2 w-8 h-8 flex items-center justify-center shadow-lg hover:bg-danger-600 transition-all transform hover:scale-110"
                                                                    title="מחק קהילה"
                                                                >
                                                                    <Trash2 size={16} strokeWidth={2.5} />
                                                                </button>
                                                            </div>

                                                            <div onClick={() => navigate('zone-details', `zone=${encodeURIComponent(zone.zone_id)}`)}>
                                                                <div className="flex items-start gap-4">
                                                                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-warning-500 to-warning-600 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                                                                        <Users size={28} className="text-white" strokeWidth={2} />
                                                                    </div>
                                                                    <div className="flex-1 min-w-0">
                                                                        <h3 className="text-lg font-bold text-ink-strong mb-1 truncate">{zone.zone_display_name || zone.zone_id}</h3>
                                                                        {zone.description && (
                                                                            <p className="text-sm text-ink-muted line-clamp-2 mb-3">{zone.description}</p>
                                                                        )}
                                                                        <div className="flex items-center gap-3 flex-wrap">
                                                                            {zone.is_private && (
                                                                                <span className="inline-flex items-center gap-1 px-3 py-1 bg-brand-50 text-brand-700 text-xs font-semibold rounded-full border border-brand-200">
                                                                                    <Shield size={12} />
                                                                                    פרטית
                                                                                </span>
                                                                            )}
                                                                            {zone.created_at && (
                                                                                <span className="text-xs text-ink-subtle">
                                                                                    נוצרה {new Date(zone.created_at).toLocaleDateString('he-IL')}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {activeTab === 'stores' && (
                                        <div className="animate-fadeIn">
                                            {stores.length === 0 ? (
                                                <div className="text-center py-20 bg-white rounded-2xl shadow-sm border border-line-subtle">
                                                    <div className="w-24 h-24 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-brand-50 to-brand-100/50 flex items-center justify-center">
                                                        <Store size={40} className="text-brand-600" strokeWidth={1.5} />
                                                    </div>
                                                    <h3 className="text-xl font-bold text-ink-strong mb-2">אין חנויות עדיין</h3>
                                                    <p className="text-sm text-ink-muted mb-6">צור חנות כדי להתחיל למכור מוצרים</p>
                                                </div>
                                            ) : (
                                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                                    {stores.map(store => (
                                                        <div
                                                            key={store.store_id}
                                                            onClick={() => window.open(`https://hamekomit.com/${store.store_slug}`, '_blank')}
                                                            className="group bg-white rounded-2xl overflow-hidden cursor-pointer hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 shadow-md border border-line-subtle"
                                                        >
                                                            {/* Cover Image Container */}
                                                            <div className="relative h-48 bg-gradient-to-br from-secondary-500 to-secondary-700 overflow-hidden">
                                                                {store.cover_image_url ? (
                                                                    <ResponsiveImage
                                                                        src={store.cover_image_url}
                                                                        alt={store.name}
                                                                        fillContainer={true}
                                                                        placeholderClassName="bg-gradient-to-br from-secondary-500 to-secondary-700"
                                                                        className="group-hover:scale-110 transition-all duration-500"
                                                                        containerClassName="absolute inset-0"
                                                                    />
                                                                ) : (
                                                                    <div className="w-full h-full flex items-center justify-center">
                                                                        <Store size={56} className="text-white/30" strokeWidth={1.5} />
                                                                    </div>
                                                                )}

                                                                {/* Overlay */}
                                                                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent group-hover:from-black/50 transition-all duration-300"></div>
                                                            </div>

                                                            {/* Content */}
                                                            <div className="p-6">
                                                                {/* Store Name */}
                                                                <h3 className="text-xl font-bold text-ink-strong mb-2 line-clamp-2 group-hover:text-secondary-500 transition-colors">
                                                                    {store.name}
                                                                </h3>

                                                                {/* About Description - Limited to 2 lines */}
                                                                {store.about && (
                                                                    <p className="text-sm text-ink-muted mb-4 line-clamp-2 leading-relaxed">
                                                                        {store.about}
                                                                    </p>
                                                                )}

                                                                {/* Status & Role Badges */}
                                                                <div className="flex flex-wrap gap-2">
                                                                    {/* Show only one badge: owner or co-admin, with modern style */}
                                                                    {store.is_owner ? (
                                                                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border border-secondary-500/30 shadow-sm bg-gradient-to-r from-secondary-500 to-secondary-500 text-white">
                                                                            <Crown size={14} strokeWidth={2.5} />
                                                                            <span>בעל החנות</span>
                                                                        </span>
                                                                    ) : (
                                                                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border border-info-600/30 shadow-sm bg-gradient-to-r from-[#4f8cff] to-info-600 text-white">
                                                                            <Shield size={14} strokeWidth={2.5} />
                                                                            <span>מנהל משנה</span>
                                                                        </span>
                                                                    )}
                                                                    {store.status === 'open' && (
                                                                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-success-50 text-success-700 border border-success-100 shadow-sm">
                                                                            <CheckCircle2 size={14} strokeWidth={2.5} />
                                                                            <span>פתוח</span>
                                                                        </span>
                                                                    )}
                                                                    {store.status === 'closed' && (
                                                                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-danger-50 text-danger-700 border border-danger-100 shadow-sm">
                                                                            <XCircle size={14} strokeWidth={2.5} />
                                                                            <span>סגור</span>
                                                                        </span>
                                                                    )}
                                                                </div>

                                                                {/* Manage Store - direct link to store admin */}
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        navigate('store-admin', `slug=${store.store_slug}`);
                                                                    }}
                                                                    className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold bg-gradient-to-r from-secondary-500 to-secondary-600 text-white hover:from-secondary-600 hover:to-secondary-700 shadow-sm hover:shadow-md transition-all"
                                                                >
                                                                    <Settings size={16} strokeWidth={2.5} />
                                                                    <span>ניהול חנות</span>
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {activeTab === 'groups' && (
                                        <div className="animate-fadeIn">
                                            <FilteredRolodexView
                                                items={groups}
                                                chatId={chatId}
                                                type="group"
                                                fromView="my-businesses"
                                                onDelete={handleGroupDeleted}
                                            />
                                        </div>
                                    )}

                                    {activeTab === 'posts' && (
                                        <div className="animate-fadeIn">
                                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                                {posts.map((post, idx) => {
                                                    if (!post) return null;
                                                    const { title, description, image } = post.data;
                                                    const pastelColors = ['bg-gradient-to-br from-warning-50 to-warning-100', 'bg-gradient-to-br from-info-50 to-info-100', 'bg-gradient-to-br from-brand-50 to-brand-100'];
                                                    const bgColor = pastelColors[idx % pastelColors.length];

                                                    return (
                                                        <div
                                                            key={post.post_id}
                                                            className={`relative ${bgColor} text-black rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden cursor-pointer group border border-line/50`}
                                                            onClick={() =>
                                                                navigate(
                                                                    'zone-details',
                                                                    `zone=${encodeURIComponent(post.zone_id)}&tab=community&highlight_post=${post.post_id}`
                                                                )
                                                            }
                                                        >
                                                            {/* Delete Button */}
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleDeletePost(post.post_id);
                                                                }}
                                                                className="absolute top-3 left-3 z-10 bg-danger-500 text-white rounded-full p-2 w-8 h-8 flex items-center justify-center shadow-lg hover:bg-danger-600 transition-all transform hover:scale-110"
                                                                title="מחק פוסט"
                                                            >
                                                                <XCircle size={16} strokeWidth={2.5} />
                                                            </button>

                                                            {/* Post Image */}
                                                            {image && (
                                                                <div className="relative h-48 overflow-hidden">
                                                                    <img
                                                                        src={image}
                                                                        alt="תמונה מהפוסט"
                                                                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                                                    />
                                                                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                                                                </div>
                                                            )}

                                                            {/* Post Content */}
                                                            <div className="p-5 space-y-2">
                                                                <h3 className="font-bold text-lg text-ink-strong line-clamp-2">{title}</h3>
                                                                <p className="text-sm text-ink-muted line-clamp-3">{description}</p>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </>
                        )}

                        {/* Favorites Tab */}
                        {mainTab === 'favorites' && (
                            <div className="animate-fadeIn">
                                <div className="mb-6">
                                    <h2 className="text-2xl font-bold text-ink-strong mb-2">רשימות המועדפים שלי</h2>
                                    <p className="text-sm text-ink-muted">נהל את רשימות המועדפים שלך</p>
                                </div>

                                {favoritesLists.length === 0 ? (
                                    <div className="text-center py-20 bg-white rounded-2xl shadow-sm border border-line-subtle">
                                        <div className="w-24 h-24 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-danger-50 to-danger-100/50 flex items-center justify-center">
                                            <Heart size={40} className="text-danger-500" strokeWidth={1.5} />
                                        </div>
                                        <h3 className="text-xl font-bold text-ink-strong mb-2">אין רשימות מועדפים עדיין</h3>
                                        <p className="text-sm text-ink-muted mb-6">התחל להוסיף עסקים ומפגשים לרשימות המועדפים שלך</p>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        {favoritesLists.map(list => {
                                            const businessCount = list.data?.businesses?.length || 0;
                                            const eventCount = list.data?.events?.length || 0;
                                            const barterCount = list.data?.barters?.length || 0;
                                            const groupCount = list.data?.groups?.length || 0;
                                            const postCount = list.data?.posts?.length || 0;
                                            const zoneCount = list.data?.zones?.length || 0;
                                            const totalCount = businessCount + eventCount + barterCount + groupCount + postCount + zoneCount;

                                            return (
                                                <div
                                                    key={list.playlist_id}
                                                    className="group relative bg-white rounded-2xl p-6 shadow-sm border border-line-subtle hover:shadow-lg transition-all duration-300 cursor-pointer hover:-translate-y-1"
                                                    onClick={() => {
                                                        setSelectedFavoritesList(list);
                                                        fetchFavoritesListData(list);
                                                    }}
                                                >
                                                    {/* Action buttons */}
                                                    <div className="absolute top-3 left-3 flex gap-2 z-10">
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleShareFavoritesList(list);
                                                            }}
                                                            className="bg-white/90 text-secondary-500 rounded-full p-2 w-8 h-8 flex items-center justify-center shadow-md hover:bg-secondary-500 hover:text-white transition-all transform hover:scale-110 border border-secondary-500/20"
                                                            title="שתף רשימה"
                                                        >
                                                            <Share2 size={16} strokeWidth={2.5} />
                                                        </button>
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleEditFavoritesList(list);
                                                            }}
                                                            className="bg-white/90 text-info-500 rounded-full p-2 w-8 h-8 flex items-center justify-center shadow-md hover:bg-info-500 hover:text-white transition-all transform hover:scale-110 border border-info-100"
                                                            title="שנה שם רשימה"
                                                        >
                                                            <Edit2 size={16} strokeWidth={2.5} />
                                                        </button>
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleDeleteFavoritesList(list.playlist_id);
                                                            }}
                                                            className="bg-white/90 text-ink-subtle rounded-full p-2 w-8 h-8 flex items-center justify-center shadow-md hover:bg-surface-muted hover:text-danger-500 transition-all transform hover:scale-110 border border-line"
                                                            title="מחק רשימה"
                                                        >
                                                            <Trash2 size={16} strokeWidth={2.5} />
                                                        </button>
                                                    </div>

                                                    <div className="flex items-start gap-4">
                                                        <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-danger-500 to-danger-600 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                                                            <Heart size={28} className="text-white fill-white" strokeWidth={2} />
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <h3 className="text-lg font-bold text-ink-strong mb-2 truncate">
                                                                {list.data?.name || 'ללא שם'}
                                                            </h3>
                                                            <div className="flex flex-wrap gap-2 mb-3">
                                                                {businessCount > 0 && (
                                                                    <span className="inline-flex items-center gap-1 px-3 py-1 bg-secondary-500/10 text-secondary-700 text-xs font-semibold rounded-full border border-secondary-500/20">
                                                                        <Briefcase size={12} />
                                                                        {businessCount} עסקים
                                                                    </span>
                                                                )}
                                                                {eventCount > 0 && (
                                                                    <span className="inline-flex items-center gap-1 px-3 py-1 bg-info-50 text-info-700 text-xs font-semibold rounded-full border border-info-100">
                                                                        <Calendar size={12} />
                                                                        {eventCount} מפגשים
                                                                    </span>
                                                                )}
                                                                {barterCount > 0 && (
                                                                    <span className="inline-flex items-center gap-1 px-3 py-1 bg-success-50 text-success-700 text-xs font-semibold rounded-full border border-success-100">
                                                                        <Handshake size={12} />
                                                                        {barterCount} ברטרים
                                                                    </span>
                                                                )}
                                                                {groupCount > 0 && (
                                                                    <span className="inline-flex items-center gap-1 px-3 py-1 bg-brand-50 text-brand-700 text-xs font-semibold rounded-full border border-brand-200">
                                                                        <Users size={12} />
                                                                        {groupCount} קבוצות
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <p className="text-sm text-ink-subtle">
                                                                סה"כ {totalCount} פריטים
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Edit Zone Modal */}
            {editingZone && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6 space-y-6">
                            {/* Header */}
                            <div className="flex items-center justify-between border-b border-line pb-4">
                                <h3 className="text-2xl font-bold text-ink-strong">ערוך קהילה</h3>
                                <button
                                    onClick={() => setEditingZone(null)}
                                    className="text-ink-subtle hover:text-ink-muted transition-colors"
                                >
                                    <XCircle size={24} />
                                </button>
                            </div>

                            {/* Zone ID (Read-only) */}
                            <div className="space-y-2 bg-surface-muted rounded-xl p-4 border border-line">
                                <label className="text-sm font-semibold text-ink-muted flex items-center gap-2">
                                    <Lock size={14} />
                                    מזהה קהילה (לא ניתן לשינוי)
                                </label>
                                <div className="font-mono text-ink-strong text-sm bg-white px-4 py-3 rounded-lg border border-line">
                                    {editingZone.zone_id}
                                </div>
                                <p className="text-xs text-ink-subtle">זהו המזהה הקבוע של הקהילה ולא ניתן לשנותו</p>
                            </div>

                            {/* Zone Display Name (Editable) */}
                            <div className="space-y-2">
                                <label className="text-sm font-semibold text-ink-strong">שם תצוגה של הקהילה</label>
                                <input
                                    type="text"
                                    value={editZoneName}
                                    onChange={(e) => setEditZoneName(e.target.value)}
                                    className="w-full px-4 py-3 rounded-xl border border-line-strong focus:border-secondary-500 focus:outline-none focus:ring-2 focus:ring-secondary-500/20 transition-all text-ink-strong"
                                    placeholder="שם הקהילה"
                                />
                                <p className="text-xs text-ink-muted">זהו השם שיוצג למשתמשים</p>
                            </div>

                            {/* Description */}
                            <div className="space-y-2">
                                <label className="text-sm font-semibold text-ink-strong">תיאור</label>
                                <textarea
                                    value={editZoneDescription}
                                    onChange={(e) => setEditZoneDescription(e.target.value)}
                                    className="w-full px-4 py-3 rounded-xl border border-line-strong focus:border-secondary-500 focus:outline-none focus:ring-2 focus:ring-secondary-500/20 transition-all resize-none text-ink-strong"
                                    rows="4"
                                    placeholder="תיאור הקהילה"
                                />
                            </div>

                            {/* Privacy Settings */}
                            <div className="space-y-3">
                                <label className="text-sm font-semibold text-ink-strong">הגדרות פרטיות</label>

                                <button
                                    type="button"
                                    onClick={() => setEditZoneIsPrivate(false)}
                                    className={`w-full rounded-xl p-4 transition-all border-2 ${!editZoneIsPrivate
                                        ? 'bg-success-50 border-success-500 shadow-md'
                                        : 'bg-white border-line hover:border-line-strong'
                                        }`}
                                >
                                    <div className="flex items-center gap-3">
                                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${!editZoneIsPrivate ? 'bg-success-500' : 'bg-line'
                                            }`}>
                                            <Globe className="w-5 h-5 text-white" strokeWidth={2.5} />
                                        </div>
                                        <div className="flex-1 text-right">
                                            <h4 className="font-bold text-base text-ink-strong">קהילה ציבורית</h4>
                                            <p className="text-sm text-ink-muted">כל משתמש יכול לראות את הקהילה</p>
                                        </div>
                                    </div>
                                </button>

                                <button
                                    type="button"
                                    onClick={() => setEditZoneIsPrivate(true)}
                                    className={`w-full rounded-xl p-4 transition-all border-2 ${editZoneIsPrivate
                                        ? 'bg-brand-50 border-brand-500 shadow-md'
                                        : 'bg-white border-line hover:border-line-strong'
                                        }`}
                                >
                                    <div className="flex items-center gap-3">
                                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${editZoneIsPrivate ? 'bg-brand-500' : 'bg-line'
                                            }`}>
                                            <Lock className="w-5 h-5 text-white" strokeWidth={2.5} />
                                        </div>
                                        <div className="flex-1 text-right">
                                            <h4 className="font-bold text-base text-ink-strong">קהילה פרטית</h4>
                                            <p className="text-sm text-ink-muted">רק משתמשים מוזמנים יכולים לראות</p>
                                        </div>
                                    </div>
                                </button>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex gap-3 pt-4">
                                <button
                                    onClick={() => setEditingZone(null)}
                                    className="flex-1 px-6 py-3 bg-surface-muted text-ink rounded-xl font-semibold hover:bg-surface-sunken transition-colors"
                                >
                                    ביטול
                                </button>
                                <button
                                    onClick={handleUpdateZone}
                                    className="flex-1 px-6 py-3 bg-gradient-to-r from-secondary-500 to-secondary-700 text-white rounded-xl font-semibold hover:shadow-lg transition-all"
                                >
                                    שמור שינויים
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Invite User Modal */}
            {invitingZone && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full">
                        <div className="p-6 space-y-6">
                            {/* Header */}
                            <div className="flex items-center justify-between border-b border-line pb-4">
                                <h3 className="text-2xl font-bold text-ink-strong">הזמן משתמשים לקהילה</h3>
                                <button
                                    onClick={handleCloseInviteModal}
                                    className="text-ink-subtle hover:text-ink-muted transition-colors"
                                >
                                    <XCircle size={24} />
                                </button>
                            </div>

                            {/* Zone Info */}
                            <div className="bg-gradient-to-br from-brand-50 to-brand-100/50 rounded-xl p-4 border border-brand-200">
                                <div className="flex items-center gap-3">
                                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-warning-500 to-warning-600 flex items-center justify-center flex-shrink-0">
                                        <Users size={24} className="text-white" strokeWidth={2} />
                                    </div>
                                    <div className="flex-1">
                                        <h4 className="font-bold text-lg text-ink-strong">{invitingZone.zone_display_name || invitingZone.zone_id}</h4>
                                        {invitingZone.description && (
                                            <p className="text-sm text-ink-muted">{invitingZone.description}</p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {!generatedInviteLink ? (
                                <>
                                    {/* Usage Limit Options */}
                                    <div className="space-y-3">
                                        <label className="text-sm font-semibold text-ink-strong">מגבלת שימושים</label>

                                        <button
                                            type="button"
                                            onClick={() => setInviteUsageLimit('unlimited')}
                                            className={`w-full rounded-xl p-4 transition-all border-2 ${inviteUsageLimit === 'unlimited'
                                                ? 'bg-info-50 border-info-500 shadow-md'
                                                : 'bg-white border-line hover:border-line-strong'
                                                }`}
                                        >
                                            <div className="flex items-center gap-3">
                                                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${inviteUsageLimit === 'unlimited' ? 'bg-info-500' : 'bg-line'}`}>
                                                    <Infinity className="w-5 h-5 text-white" strokeWidth={2.5} />
                                                </div>
                                                <div className="flex-1 text-right">
                                                    <h4 className="font-bold text-base text-ink-strong">שימושים ללא הגבלה</h4>
                                                    <p className="text-sm text-ink-muted">הקישור יהיה תקף לתמיד</p>
                                                </div>
                                            </div>
                                        </button>

                                        <button
                                            type="button"
                                            onClick={() => setInviteUsageLimit('limited')}
                                            className={`w-full rounded-xl p-4 transition-all border-2 ${inviteUsageLimit === 'limited'
                                                ? 'bg-warning-50 border-warning-500 shadow-md'
                                                : 'bg-white border-line hover:border-line-strong'
                                                }`}
                                        >
                                            <div className="flex items-center gap-3">
                                                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${inviteUsageLimit === 'limited' ? 'bg-warning-500' : 'bg-line'}`}>
                                                    <Link2 className="w-5 h-5 text-white" strokeWidth={2.5} />
                                                </div>
                                                <div className="flex-1 text-right">
                                                    <h4 className="font-bold text-base text-ink-strong">מספר שימושים מוגבל</h4>
                                                    <p className="text-sm text-ink-muted">הגדר מספר שימושים מקסימלי</p>
                                                </div>
                                            </div>
                                        </button>
                                    </div>

                                    {/* Max Uses Input (shown when limited is selected) */}
                                    {inviteUsageLimit === 'limited' && (
                                        <div className="space-y-2">
                                            <label className="text-sm font-semibold text-ink-strong">מספר שימושים מקסימלי</label>
                                            <input
                                                type="number"
                                                min="1"
                                                value={inviteMaxUses}
                                                onChange={(e) => setInviteMaxUses(parseInt(e.target.value) || 1)}
                                                className="w-full px-4 py-3 rounded-xl border border-line-strong focus:border-warning-500 focus:outline-none focus:ring-2 focus:ring-warning-500/20 transition-all text-ink-strong"
                                                placeholder="10"
                                            />
                                            <p className="text-xs text-ink-muted">הקישור יפוג לאחר מספר השימושים הזה</p>
                                        </div>
                                    )}

                                    {/* Action Buttons */}
                                    <div className="flex gap-3 pt-4">
                                        <button
                                            onClick={handleCloseInviteModal}
                                            className="flex-1 px-6 py-3 bg-surface-muted text-ink rounded-xl font-semibold hover:bg-surface-sunken transition-colors"
                                        >
                                            ביטול
                                        </button>
                                        <button
                                            onClick={handleGenerateInviteLink}
                                            className="flex-1 px-6 py-3 bg-gradient-to-r from-success-500 to-success-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all flex items-center justify-center gap-2"
                                        >
                                            <Link2 size={18} strokeWidth={2.5} />
                                            צור קישור הזמנה
                                        </button>
                                    </div>
                                </>
                            ) : (
                                <>
                                    {/* Generated Link Display */}
                                    <div className="space-y-3">
                                        <label className="text-sm font-semibold text-ink-strong">קישור ההזמנה שלך</label>
                                        <div className="relative">
                                            <div className="w-full px-4 py-3 rounded-xl border-2 border-success-500 bg-success-50 text-ink-strong font-mono text-sm break-all pr-12">
                                                {generatedInviteLink}
                                            </div>
                                            <button
                                                onClick={handleCopyInviteLink}
                                                className={`absolute left-2 top-1/2 -translate-y-1/2 p-2 rounded-lg transition-all ${linkCopied
                                                    ? 'bg-success-600 text-white'
                                                    : 'bg-success-500 text-white hover:bg-success-600'
                                                    }`}
                                                title="העתק קישור"
                                            >
                                                {linkCopied ? <Check size={20} strokeWidth={2.5} /> : <Copy size={20} strokeWidth={2.5} />}
                                            </button>
                                        </div>
                                        <p className="text-xs text-ink-muted">
                                            {inviteUsageLimit === 'unlimited'
                                                ? 'קישור זה תקף ללא הגבלת שימושים'
                                                : `קישור זה תקף ל-${inviteMaxUses} שימושים`}
                                        </p>
                                    </div>

                                    {/* Action Buttons */}
                                    <div className="flex gap-3 pt-4">
                                        <button
                                            onClick={handleCloseInviteModal}
                                            className="flex-1 px-6 py-3 bg-gradient-to-r from-secondary-500 to-secondary-700 text-white rounded-xl font-semibold hover:shadow-lg transition-all"
                                        >
                                            סגור
                                        </button>
                                        <button
                                            onClick={handleCopyInviteLink}
                                            className="flex-1 px-6 py-3 bg-gradient-to-r from-success-500 to-success-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all flex items-center justify-center gap-2"
                                        >
                                            <Copy size={18} strokeWidth={2.5} />
                                            העתק קישור
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Favorites List Modal */}
            {selectedFavoritesList && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[99999] flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full h-[90vh] flex flex-col">
                        {/* Header */}
                        <div className="p-6 border-b border-line flex-shrink-0">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-danger-500 to-danger-600 flex items-center justify-center flex-shrink-0">
                                        <Heart size={24} className="text-white fill-white" strokeWidth={2} />
                                    </div>
                                    <div>
                                        <h3 className="text-2xl font-bold text-ink-strong">
                                            {selectedFavoritesList.data?.name || 'ללא שם'}
                                        </h3>
                                        <p className="text-sm text-ink-muted">
                                            {(() => {
                                                const businessCount = selectedFavoritesList.data?.businesses?.length || 0;
                                                const eventCount = selectedFavoritesList.data?.events?.length || 0;
                                                const barterCount = selectedFavoritesList.data?.barters?.length || 0;
                                                const groupCount = selectedFavoritesList.data?.groups?.length || 0;
                                                const totalCount = businessCount + eventCount + barterCount + groupCount;
                                                return `סה"כ ${totalCount} פריטים`;
                                            })()}
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => {
                                        setSelectedFavoritesList(null);
                                        setExpandedFavoritesData(null);
                                    }}
                                    className="text-ink-subtle hover:text-ink-muted transition-colors"
                                >
                                    <XCircle size={24} />
                                </button>
                            </div>
                        </div>

                        {/* Content */}
                        <div className="flex-1 overflow-y-auto p-6">
                            {loadingFavoritesData ? (
                                <div className="flex justify-center items-center py-20">
                                    <div className="w-12 h-12 border-4 border-secondary-500 border-t-transparent rounded-full animate-spin"></div>
                                </div>
                            ) : expandedFavoritesData ? (
                                (() => {
                                    const businesses = expandedFavoritesData.businesses || [];
                                    const events = expandedFavoritesData.events || [];
                                    const barters = expandedFavoritesData.barters || [];
                                    const groups = expandedFavoritesData.groups || [];
                                    const hasNoItems = businesses.length === 0 && events.length === 0 && barters.length === 0 && groups.length === 0;

                                    if (hasNoItems) {
                                        return (
                                            <div className="text-center py-20">
                                                <Heart size={48} className="text-ink-subtle mx-auto mb-4" />
                                                <p className="text-ink-muted">רשימה זו ריקה</p>
                                            </div>
                                        );
                                    }

                                    return (
                                        <div className="space-y-6">
                                            {/* Businesses */}
                                            {businesses.length > 0 && (
                                                <div>
                                                    <h4 className="text-lg font-bold text-ink-strong mb-3 flex items-center gap-2">
                                                        <Briefcase size={20} className="text-secondary-500" />
                                                        עסקים ({businesses.length})
                                                    </h4>
                                                    <FilteredRolodexView
                                                        items={businesses}
                                                        chatId={chatId}
                                                        fromView='favorites-list'
                                                        onDelete={(businessId) => handleRemoveFromFavoritesList(businessId, 'businesses')}
                                                    />
                                                </div>
                                            )}

                                            {/* Events */}
                                            {events.length > 0 && (
                                                <div>
                                                    <h4 className="text-lg font-bold text-ink-strong mb-3 flex items-center gap-2">
                                                        <Calendar size={20} className="text-info-600" />
                                                        מפגשים ({events.length})
                                                    </h4>
                                                    <FilteredRolodexView
                                                        items={events}
                                                        chatId={chatId}
                                                        fromView='favorites-list'
                                                        onDelete={(eventId) => handleRemoveFromFavoritesList(eventId, 'events')}
                                                    />
                                                </div>
                                            )}

                                            {/* Barters */}
                                            {barters.length > 0 && (
                                                <div>
                                                    <h4 className="text-lg font-bold text-ink-strong mb-3 flex items-center gap-2">
                                                        <Handshake size={20} className="text-success-600" />
                                                        ברטרים ({barters.length})
                                                    </h4>
                                                    <FilteredRolodexView
                                                        items={barters}
                                                        chatId={chatId}
                                                        fromView='favorites-list'
                                                        onDelete={(barterId) => handleRemoveFromFavoritesList(barterId, 'barters')}
                                                    />
                                                </div>
                                            )}

                                            {/* Groups */}
                                            {groups.length > 0 && (
                                                <div>
                                                    <h4 className="text-lg font-bold text-ink-strong mb-3 flex items-center gap-2">
                                                        <Users size={20} className="text-brand-600" />
                                                        קבוצות ({groups.length})
                                                    </h4>
                                                    <FilteredRolodexView
                                                        items={groups}
                                                        chatId={chatId}
                                                        type="group"
                                                        fromView='favorites-list'
                                                        onDelete={(groupId) => handleRemoveFromFavoritesList(groupId, 'groups')}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    );
                                })()
                            ) : null}
                        </div>

                        {/* Footer */}
                        <div className="p-6 border-t border-line flex-shrink-0">
                            <button
                                onClick={() => {
                                    setSelectedFavoritesList(null);
                                    setExpandedFavoritesData(null);
                                }}
                                className="w-full px-6 py-3 bg-gradient-to-r from-secondary-500 to-secondary-700 text-white rounded-xl font-semibold hover:shadow-lg transition-all"
                            >
                                סגור
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Favorites List Modal */}
            {editingFavoritesList && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full">
                        <div className="p-6 space-y-6">
                            {/* Header */}
                            <div className="flex items-center justify-between border-b border-line pb-4">
                                <h3 className="text-2xl font-bold text-ink-strong">ערוך רשימת מועדפים</h3>
                                <button
                                    onClick={() => setEditingFavoritesList(null)}
                                    className="text-ink-subtle hover:text-ink-muted transition-colors"
                                >
                                    <XCircle size={24} />
                                </button>
                            </div>

                            {/* List Name */}
                            <div className="space-y-2">
                                <label className="text-sm font-semibold text-ink-strong">שם הרשימה</label>
                                <input
                                    type="text"
                                    value={editListName}
                                    onChange={(e) => setEditListName(e.target.value)}
                                    className="w-full px-4 py-3 rounded-xl border border-line-strong focus:border-secondary-500 focus:outline-none focus:ring-2 focus:ring-secondary-500/20 transition-all text-ink-strong"
                                    placeholder="שם הרשימה"
                                />
                            </div>

                            {/* Action Buttons */}
                            <div className="flex gap-3 pt-4">
                                <button
                                    onClick={() => setEditingFavoritesList(null)}
                                    className="flex-1 px-6 py-3 bg-surface-muted text-ink rounded-xl font-semibold hover:bg-surface-sunken transition-colors"
                                >
                                    ביטול
                                </button>
                                <button
                                    onClick={handleUpdateFavoritesList}
                                    className="flex-1 px-6 py-3 bg-gradient-to-r from-secondary-500 to-secondary-700 text-white rounded-xl font-semibold hover:shadow-lg transition-all"
                                >
                                    שמור שינויים
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Share Options Modal */}
            {sharingList && (
                <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={() => setSharingList(null)}>
                    <div
                        className="w-full max-w-md bg-white rounded-3xl shadow-2xl transform transition-all duration-300 ease-out"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div className="p-6 border-b border-line-subtle">
                            <div className="flex items-center gap-3">
                                <Share2 size={24} className="text-info-600" />
                                <div className="flex-1">
                                    <h3 className="text-xl font-bold text-ink-strong">שיתוף רשימה</h3>
                                    <p className="text-sm text-ink-muted mt-1">{sharingList.data?.name || 'רשימת מועדפים'}</p>
                                </div>
                            </div>
                        </div>

                        {/* Share Options */}
                        <div className="p-4 space-y-3">
                            {/* WhatsApp */}
                            <button
                                onClick={() => handleShareOption('whatsapp')}
                                className="w-full flex items-center gap-4 p-4 bg-gradient-to-r from-success-50 to-success-100 hover:from-success-100 hover:to-success-100 rounded-xl transition-all duration-200 border border-success-100 hover:shadow-md"
                            >
                                <div className="w-12 h-12 rounded-full bg-success-500 flex items-center justify-center flex-shrink-0">
                                    <MessageCircle size={24} className="text-white" />
                                </div>
                                <div className="flex-1 text-right">
                                    <h4 className="font-bold text-ink-strong">שתף בווטסאפ</h4>
                                    <p className="text-sm text-ink-muted">שלח את הרשימה לחברים</p>
                                </div>
                            </button>

                            {/* Telegram */}
                            <button
                                onClick={() => handleShareOption('telegram')}
                                className="w-full flex items-center gap-4 p-4 bg-gradient-to-r from-info-50 to-info-100 hover:from-info-100 hover:to-info-100 rounded-xl transition-all duration-200 border border-info-100 hover:shadow-md"
                            >
                                <div className="w-12 h-12 rounded-full bg-info-500 flex items-center justify-center flex-shrink-0">
                                    <Send size={24} className="text-white" />
                                </div>
                                <div className="flex-1 text-right">
                                    <h4 className="font-bold text-ink-strong">שתף בטלגרם</h4>
                                    <p className="text-sm text-ink-muted">שלח את הרשימה בטלגרם</p>
                                </div>
                            </button>

                            {/* Copy Link */}
                            <button
                                onClick={() => handleShareOption('copy')}
                                className={`w-full flex items-center gap-4 p-4 rounded-xl transition-all duration-200 border ${shareLinkCopied
                                    ? 'bg-gradient-to-r from-success-50 to-success-100 border-success-100'
                                    : 'bg-gradient-to-r from-surface-muted to-surface-muted hover:from-surface-muted hover:to-surface-sunken border-line hover:shadow-md'
                                    }`}
                                disabled={shareLinkCopied}
                            >
                                <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${shareLinkCopied ? 'bg-success-500' : 'bg-ink-muted'
                                    }`}>
                                    {shareLinkCopied ? (
                                        <Check size={24} className="text-white" />
                                    ) : (
                                        <Copy size={24} className="text-white" />
                                    )}
                                </div>
                                <div className="flex-1 text-right">
                                    <h4 className="font-bold text-ink-strong">
                                        {shareLinkCopied ? 'הקישור הועתק!' : 'העתק קישור'}
                                    </h4>
                                    <p className="text-sm text-ink-muted">
                                        {shareLinkCopied ? 'כעת תוכל להדביק אותו בכל מקום' : 'העתק את הקישור ללוח'}
                                    </p>
                                </div>
                            </button>
                        </div>

                        {/* Cancel Button */}
                        <div className="p-4 pt-2">
                            <button
                                onClick={() => setSharingList(null)}
                                className="w-full px-6 py-3 bg-surface-muted text-ink rounded-xl font-semibold hover:bg-surface-sunken transition-colors"
                            >
                                ביטול
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};